import { api } from "@/lib/api";
import { Appointment } from "@/types";
import {
  AppointmentApiResponse,
  AppointmentApiSingleResponse,
  AppointmentQueryParams,
  AppointmentCreateInput,
  AppointmentUpdateInput,
  transformAppointmentFromApi,
  transformAppointmentToApi,
  PaginationMeta,
} from "@/types/appointment.api.types";
import {
  AppoinmentInput,
  AppointmentServiceCreateInput,
  AppoinmentOutput,
} from "@/types/appoinment.types";
import { mapFrontendStatusToBackend } from "@/lib/appointmentStatus";

export interface AppointmentListResponse {
  appointments: Appointment[];
  pagination?: PaginationMeta;
}

export interface UpcomingAppointmentsResponse {
  success: boolean;
  data: AppoinmentOutput[];
  message?: string;
}

export const appointmentService = {
  async getAllAppointments(
    params?: AppointmentQueryParams,
  ): Promise<AppointmentListResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.status) queryParams.append("status", params.status);
      if (params?.customerId)
        queryParams.append("customerId", params.customerId);
      if (params?.staffId) queryParams.append("staffId", params.staffId);
      if (params?.serviceType)
        queryParams.append("serviceType", params.serviceType);
      if (params?.startDate) queryParams.append("startDate", params.startDate);
      if (params?.endDate) queryParams.append("endDate", params.endDate);
      if (params?.search)
        queryParams.append("search", encodeURIComponent(params.search));
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());

      const queryString = queryParams.toString();
      const endpoint = queryString
        ? `/appointments?${queryString}`
        : "/appointments";

      const response = await api.get<AppointmentApiResponse>(endpoint);

      // Transform API response to frontend format
      const appointments = response.data.data.map((apt) =>
        transformAppointmentFromApi(apt),
      );

      return {
        appointments,
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error("Error fetching appointments:", error);
      throw error;
    }
  },

  async getAppointmentById(id: string): Promise<Appointment> {
    try {
      const response = await api.get<AppointmentApiSingleResponse>(
        `/appointments/${id}`,
      );
      return transformAppointmentFromApi(response.data.data);
    } catch (error) {
      console.error("Error fetching appointment:", error);
      throw error;
    }
  },

  async createAppointment(
    appointment: AppointmentCreateInput,
  ): Promise<Appointment> {
    try {
      // Determine which endpoint to use based on appointment type
      const endpoint =
        appointment.type === "USER_SUBSCRIPTION"
          ? "/appointments/user-subscription"
          : "/appointments";

      // Build a flexible payload for the backend. Some backends expect
      // a `details` array for recurring appointments (day/time), so map
      // `recurringDay` + `preferredTime` into that shape when present.
      const apiInput: any = {
        type: appointment.type,
        typeId: appointment.typeId,
        date: appointment.date,
        status: appointment.status,
        // Map frontend paymentMode to backend expected string when necessary
        paymentMode: (() => {
          const pm = String(appointment.paymentMode || "");
          const map: Record<string, string> = {
            G_PAY: "G-PAY",
          };
          return map[pm] || pm;
        })(),
        vehicleIds: appointment.vehicleIds || [],
        customerId: appointment.customerId,
        ...(appointment.staffId && { staffId: appointment.staffId }),
        ...(appointment.bookingRef && { bookingRef: appointment.bookingRef }),
        // Include bookingType if provided (ONLINE, OFFLINE, SUBSCRIPTION)
        ...(appointment.bookingType && {
          bookingType: appointment.bookingType,
        }),
        // include amount/address/discount if provided
        ...(appointment.amount !== undefined && { amount: appointment.amount }),
        // Include subtotal if provided (overrides calculated price)
        ...(appointment.subtotal !== undefined && {
          subtotal: appointment.subtotal,
        }),
        ...(appointment.address !== undefined && {
          address: appointment.address,
        }),
        ...((appointment as any).discount !== undefined && {
          discount: (appointment as any).discount,
        }),
        ...(appointment.type === "USER_SUBSCRIPTION" && appointment.ruleId
          ? { ruleId: appointment.ruleId }
          : {}),
        // For USER_SUBSCRIPTION, map repeatedAppointmentId to repeatedAppointmentSubscriptionId
        ...(appointment.repeatedAppointmentId && {
          ...(appointment.type === "USER_SUBSCRIPTION"
            ? {
                repeatedAppointmentSubscriptionId:
                  appointment.repeatedAppointmentId,
              }
            : { repeatedAppointmentId: appointment.repeatedAppointmentId }),
        }),
      };

      // Map recurring fields to `details` array expected by backend (if present)
      if (appointment.recurringDay && appointment.preferredTime) {
        const formatDay = String(appointment.recurringDay).toUpperCase();
        const formatTime = String(appointment.preferredTime)
          .replace(/\s+/g, "")
          .toUpperCase();
        apiInput.details = [
          {
            day: formatDay,
            time: formatTime,
          },
        ];
      }

      const response = await api.post<AppointmentApiSingleResponse>(
        endpoint,
        apiInput,
      );
      return transformAppointmentFromApi(response.data.data);
    } catch (error) {
      console.error("Error creating appointment:", error);
      throw error;
    }
  },

  async updateAppointment(
    id: string,
    appointment:
      | AppointmentUpdateInput
      | Partial<AppoinmentInput | AppointmentServiceCreateInput>,
  ): Promise<Appointment> {
    try {
      const apiInput = appointment as Partial<
        AppoinmentInput | AppointmentServiceCreateInput
      >;

      const response = await api.put<AppointmentApiSingleResponse>(
        `/appointments/${id}`,
        apiInput,
      );
      return transformAppointmentFromApi(
        response.data.data,
        appointment as Partial<Appointment>,
      );
    } catch (error) {
      console.error("Error updating appointment:", error);
      throw error;
    }
  },

  async updateAppointmentStatus(
    id: string,
    status: Appointment["status"],
  ): Promise<Appointment> {
    try {
      // Status is already in the correct API format (PENDING, CONFIRMED, COMPLETED, CANCELLED)
      // Only map if it's a legacy status value
      const backendStatus = mapFrontendStatusToBackend(status);
      const response = await api.patch<AppointmentApiSingleResponse>(
        `/appointments/${id}/status`,
        {
          status: backendStatus,
        },
      );
      return transformAppointmentFromApi(response.data.data, { status });
    } catch (error) {
      console.error("Error updating appointment status:", error);
      throw error;
    }
  },

  async deleteAppointment(id: string): Promise<void> {
    try {
      await api.delete(`/appointments/${id}`);
    } catch (error) {
      console.error("Error deleting appointment:", error);
      throw error;
    }
  },

  async getUpcomingAppointments(
    limit: number = 10,
    status?: string,
  ): Promise<Appointment[]> {
    try {
      const params = new URLSearchParams();
      if (limit !== undefined) params.append("limit", limit.toString());
      if (status) params.append("status", status);

      const queryString = params.toString();
      const url = `/dashboard/upcoming-appointments${queryString ? `?${queryString}` : ""}`;

      const response = await api.get<UpcomingAppointmentsResponse>(url);
      return response.data.data.map((apt) => transformAppointmentFromApi(apt));
    } catch (error) {
      console.error("Error fetching upcoming appointments:", error);
      throw error;
    }
  },

  /**
   * Get recurring appointments for a customer (excludes COMPLETED/CANCELLED by default)
   * Used in: CustomerRecurringAppointments section in edit dialog
   *
   * NOTE: Falls back to regular appointments endpoint if recurring endpoint returns empty,
   * since some repeated appointments may not have ruleId set.
   */
  async getRecurringByCustomerId(
    customerId: string,
    options?: {
      excludeStatuses?: string[];
      page?: number;
      limit?: number;
    },
  ): Promise<{
    data: import("@/types/recurring-appointments.types").RecurringAppointment[];
    pagination: import("@/types/recurring-appointments.types").RecurringPaginationMeta;
  }> {
    try {
      const params = new URLSearchParams();

      if (options?.excludeStatuses?.length) {
        params.set("excludeStatuses", options.excludeStatuses.join(","));
      }
      if (options?.page) {
        params.set("page", options.page.toString());
      }
      if (options?.limit) {
        params.set("limit", options.limit.toString());
      }

      const queryString = params.toString();
      const url = `/appointments/recurring/customer/${customerId}${
        queryString ? `?${queryString}` : ""
      }`;

      const response =
        await api.get<
          import("@/types/recurring-appointments.types").RecurringAppointmentsResponse
        >(url);

      // If recurring endpoint returned data, use it
      if (response.data.data && response.data.data.length > 0) {
        return {
          data: response.data.data,
          pagination: response.data.pagination,
        };
      }

      // Fallback: fetch regular appointments for this customer and filter
      console.log(
        "Recurring endpoint returned empty, falling back to regular appointments",
      );

      const excludeStatuses = options?.excludeStatuses || [
        "COMPLETED",
        "CANCELLED",
      ];
      const fallbackResponse = await api.get<AppointmentApiResponse>(
        `/appointments?customerId=${customerId}&limit=${options?.limit || 50}`,
      );

      // Filter out excluded statuses and transform to expected format
      const filteredAppointments = (fallbackResponse.data.data || [])
        .filter((apt) => !excludeStatuses.includes(apt.status))
        .map(
          (
            apt,
          ): import("@/types/recurring-appointments.types").RecurringAppointment => ({
            id: apt.id,
            bookingRef: apt.bookingRef || null,
            type: apt.type as "PACKAGE" | "USER_SUBSCRIPTION",
            typeId: apt.typeId,
            serviceName: apt.serviceName,
            customerName: apt.customerName || null,
            status: apt.status,
            date: apt.date,
            paymentMode: apt.paymentMode,
            customerId: apt.customerId,
            staffId: apt.staffId || null,
            vehicleIds: apt.vehicleIds || [],
            vehicles: (apt.vehicleIds || []).map((vId) => ({
              id: vId,
              plateNumber: vId, // Will be resolved by component if needed
              make: "",
              model: "",
            })),
            address: apt.address || null,
            remarks: apt.remarks || null,
            ruleId: apt.ruleId || null,
            amount: apt.amount || null,
            discount: apt.discount || null,
            isDeleted: apt.isDeleted,
            createdAt: apt.createdAt,
            updatedAt: apt.updatedAt,
          }),
        );

      return {
        data: filteredAppointments,
        pagination: {
          page: options?.page || 1,
          limit: options?.limit || 50,
          total: filteredAppointments.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      };
    } catch (error) {
      console.error("Error fetching recurring appointments:", error);
      throw error;
    }
  },

  /**
   * Update only the vehicles for an appointment (triggers price recalculation)
   * Used in: EditAppointmentVehiclesDialog
   */
  async updateAppointmentVehicles(
    appointmentId: string,
    vehicleIds: string[],
  ): Promise<
    import("@/types/recurring-appointments.types").UpdateVehiclesResponse["data"]
  > {
    try {
      if (!vehicleIds.length) {
        throw new Error("At least one vehicle is required");
      }

      const response = await api.patch<
        import("@/types/recurring-appointments.types").UpdateVehiclesResponse
      >(`/appointments/${appointmentId}/vehicles`, { vehicleIds });

      return response.data.data;
    } catch (error) {
      console.error("Error updating appointment vehicles:", error);
      throw error;
    }
  },
};

// Status mapping lives in @/lib/appointmentStatus — imported above.
